import { Reveal } from "@/components/Reveal";
import { Siren, Navigation, MailWarning, PhoneCall } from "lucide-react";

const points = [
  {
    icon: Siren,
    title: "Convoy-wide, instantly",
    body: "One tap broadcasts your name and live position to every member of your convoy — no fumbling for a phone call.",
  },
  {
    icon: Navigation,
    title: "Navigate to sender",
    body: "Members get a one-tap route straight to your exact spot, so help rides to you instead of searching for you.",
  },
  {
    icon: MailWarning,
    title: "Priority SOS",
    body: "On Premium, an SOS also emails every other convoy member — so the alert lands even when phones are pocketed.",
  },
];

export const SosStory = () => (
  <section className="relative overflow-hidden py-24 sm:py-32" data-testid="sos-story-section">
    <div className="pointer-events-none absolute left-1/2 top-1/3 h-96 w-[50rem] -translate-x-1/2 rounded-full bg-[#FF3333]/10 blur-[140px]" aria-hidden="true" />
    <div className="mx-auto grid max-w-6xl items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20">
      <div className="relative order-2 lg:order-1">
        <Reveal>
          <div className="relative mx-auto w-fit lg:mx-0">
            <div className="pointer-events-none absolute -inset-10 rounded-full bg-amber-500/15 blur-[70px]" aria-hidden="true" />
            <div className="relative w-[250px] overflow-hidden rounded-[2rem] border border-amber-400/30 shadow-[0_30px_90px_rgba(255,51,51,0.25)] sm:w-[280px]" data-testid="sos-screenshot">
              <img
                src="/assets/shot-sos.webp"
                alt="JomKonvoi Priority SOS alert showing the sender's location with navigate to sender and acknowledge actions"
                loading="lazy"
                className="block w-full"
              />
            </div>
          </div>
        </Reveal>
      </div>

      <div className="order-1 lg:order-2">
        <Reveal>
          <p className="label-tech text-[#FF3333]">Safety, built in</p>
          <h2 className="mt-4 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
            One tap.
            <br />
            The whole convoy knows.
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-neutral-400">
            Breakdown, wrong turn, or something worse — SOS turns your convoy into your safety net.
            Everyone sees the alert, everyone sees where you are, and anyone can navigate straight
            to you.
          </p>
        </Reveal>

        <div className="mt-10 space-y-6">
          {points.map((p, i) => (
            <Reveal key={p.title} delay={0.08 + i * 0.08}>
              <div className="flex items-start gap-4" data-testid={`sos-point-${i}`}>
                <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#FF3333]/30 bg-[#FF3333]/10">
                  <p.icon className="h-5 w-5 text-[#FF3333]" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold text-white">{p.title}</h3>
                  <p className="mt-1 max-w-md text-sm leading-relaxed text-neutral-400">{p.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.3}>
          <div className="mt-10 flex items-start gap-3 rounded-2xl border border-amber-400/20 bg-amber-400/5 p-5" data-testid="sos-disclaimer">
            <PhoneCall className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
            <p className="text-sm leading-relaxed text-neutral-300">
              SOS is a coordination signal, not an emergency service. In a real emergency, dial{" "}
              <span className="font-semibold text-white">999</span> in Malaysia (or your local
              emergency number) first — then alert your convoy.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
